// ---------------------------------------------------------------------------
// ActionFlow Editor — Core Data Models
//
// 1:1 mapping from Flutter actionflow_models.dart (1,258 LOC → ~550 LOC)
// ---------------------------------------------------------------------------

import { getVisualCategoryFromRegistry } from '@/lib/action-constants'
import type { FlowTrigger } from '@cogo/types-v2'

// ---- Node Types (8) ----

export type ActionNodeType =
  | 'trigger'       // Entry point — no input port
  | 'basic'         // Single action execution
  | 'callback'      // Action with success/failure callbacks
  | 'conditional'   // If/else branching
  | 'loop'          // Iteration
  | 'switchNode'    // Multi-branch selection
  | 'group'         // Container for nested nodes
  | 'stickyNote'    // Annotation (non-executable)

// ---- Connection Types (5) ----

export type ConnectionType =
  | 'next'
  | 'onSuccess'
  | 'onFailure'
  | 'conditionTrue'
  | 'conditionFalse'

// ---- Visual Categories (8) ----

export type VisualCategory =
  | 'general'
  | 'dataFetch'
  | 'dataMutate'
  | 'navigation'
  | 'uiFeedback'
  | 'form'
  | 'flowControl'
  | 'auth'
  | 'utility'
  // Legacy aliases (mapped to new categories in registry)
  | 'apiData'
  | 'uiAction'

// ---- Execution Status ----

export type NodeExecutionStatus = 'idle' | 'running' | 'success' | 'error'

// ---- Port Validation ----

export type PortValidationState = 'normal' | 'validTarget' | 'occupied' | 'invalid'

// ---- Dimensions (n8n v3 style, Flutter 1:1) ----

export const NODE_WIDTH = 96
export const NODE_HEIGHT = 96
export const NODE_HEADER_HEIGHT = 36
export const PORT_RADIUS = 6
export const PORT_HIT_RADIUS = 12
export const GROUP_WIDTH = 480
export const GROUP_HEIGHT = 300
export const EXTERNAL_LABEL_HEIGHT = 36
export const QUICK_BAR_HEIGHT = 36

// ---- Semantic Colors ----

export const CONNECTION_COLORS: Record<ConnectionType, string> = {
  next: '#9ca3af',          // gray-400
  onSuccess: 'hsl(var(--success))',
  onFailure: '#ef4444',     // red-500
  conditionTrue: 'hsl(var(--success))',
  conditionFalse: '#f59e0b', // amber-500
}

export const CATEGORY_BADGE_COLORS: Record<string, string> = {
  general: 'bg-[hsl(var(--brand-output))]',
  dataFetch: 'bg-primary',
  dataMutate: 'bg-primary',
  navigation: 'bg-primary',
  uiFeedback: 'bg-[hsl(var(--brand-output))]',
  form: 'bg-[hsl(var(--brand-output))]',
  flowControl: 'bg-amber-500',
  auth: 'bg-rose-500',
  utility: 'bg-gray-500',
  // Legacy aliases
  apiData: 'bg-primary',
  uiAction: 'bg-[hsl(var(--brand-output))]',
}

export const CATEGORY_ICONS: Record<string, string> = {
  general: 'Play',
  dataFetch: 'Database',
  dataMutate: 'Pencil',
  navigation: 'Compass',
  uiFeedback: 'Bell',
  form: 'CheckSquare',
  flowControl: 'GitBranch',
  auth: 'Shield',
  utility: 'MoreHorizontal',
  // Legacy aliases
  apiData: 'Globe',
  uiAction: 'Layout',
}

// ---- Core Interfaces ----

export interface ActionFlowNode {
  id: string
  nodeType: ActionNodeType
  actionType: string
  label: string
  description?: string
  position: { x: number; y: number }
  parameters: Record<string, unknown>
  connections: Partial<Record<ConnectionType, string | null>>
  parentId?: string
  childNodeIds: string[]
  isCollapsed: boolean
  executionStatus: NodeExecutionStatus
  executionDetail?: string
}

export interface ActionFlowConnection {
  id: string
  sourceNodeId: string
  targetNodeId: string
  connectionType: ConnectionType
  outputIndex: number
}

export interface FlowCanvasState {
  nodes: ActionFlowNode[]
  connections: ActionFlowConnection[]
  selectedNodeIds: string[]
  drawingConnection: {
    fromNodeId: string
    connectionType: ConnectionType
    outputIndex: number
  } | null
}

export interface FlowMeta {
  id: string
  name: string
  trigger: FlowTrigger
  narrative?: import('@cogo/types').Narrative | null
}

// ---- Helpers ----

/** Get the output port types for a given node type. */
export function getOutputPortTypes(nodeType: ActionNodeType): ConnectionType[] {
  switch (nodeType) {
    case 'trigger':
      return ['next']
    case 'basic':
      return ['next']
    case 'callback':
      return ['onSuccess', 'onFailure']
    case 'conditional':
      return ['conditionTrue', 'conditionFalse']
    case 'loop':
      return ['next']
    case 'switchNode':
      return [] // dynamic — determined by parameters.cases
    case 'group':
      return ['next']
    case 'stickyNote':
      return []
  }
}

/** Get the visual category for a node based on its type and actionType. */
export function getVisualCategory(nodeType: ActionNodeType, actionType: string): VisualCategory {
  // Trigger is always general
  if (nodeType === 'trigger') return 'general'

  // Flow control types
  if (nodeType === 'conditional' || nodeType === 'loop' || nodeType === 'switchNode') {
    return 'flowControl'
  }

  // By actionType — lookup from action-registry (SSoT)
  return getVisualCategoryFromRegistry(actionType) ?? 'general'
}

/** Compute a deterministic connection ID. */
export function computeConnectionId(
  sourceId: string,
  type: ConnectionType,
  outputIndex: number,
  targetId: string,
): string {
  return `${sourceId}_${type}_${outputIndex}_${targetId}`
}

/** Calculate node height based on output port count (n8n formula). */
export function getNodeHeight(outputCount: number): number {
  if (outputCount <= 2) return NODE_HEIGHT
  return NODE_HEIGHT + (outputCount - 2) * 24
}

/** Effective output index for a connection type. */
export function effectiveOutputIndex(type: ConnectionType, explicitIndex?: number): number {
  if (explicitIndex !== undefined && explicitIndex !== 0) return explicitIndex
  switch (type) {
    case 'conditionTrue':
    case 'onSuccess':
      return 0
    case 'conditionFalse':
    case 'onFailure':
      return 1
    default:
      return 0
  }
}

/** Create a default node. */
export function createNode(
  id: string,
  nodeType: ActionNodeType,
  actionType: string,
  position: { x: number; y: number },
  label?: string,
): ActionFlowNode {
  return {
    id,
    nodeType,
    actionType,
    label: label ?? actionType,
    position,
    parameters: {},
    connections: {},
    childNodeIds: [],
    isCollapsed: false,
    executionStatus: 'idle',
  }
}

/** Create a connection model. */
export function createConnection(
  sourceNodeId: string,
  targetNodeId: string,
  connectionType: ConnectionType,
  outputIndex: number = 0,
): ActionFlowConnection {
  return {
    id: computeConnectionId(sourceNodeId, connectionType, outputIndex, targetNodeId),
    sourceNodeId,
    targetNodeId,
    connectionType,
    outputIndex,
  }
}

/** Get switch node output count from parameters. */
export function getSwitchOutputCount(parameters: Record<string, unknown>): number {
  const cases = parameters['cases'] as string[] | undefined
  return cases ? cases.length + 1 : 3  // +1 for default
}

/** Switch node port ID list (switch_0, switch_1, ..., switch_default). */
export function getSwitchPorts(parameters: Record<string, unknown>): string[] {
  const cases = (parameters['cases'] as string[]) ?? ['case1', 'case2']
  return [...cases.map((_, i) => `switch_${i}`), 'switch_default']
}

/** Label for a Switch port. */
export function getSwitchPortLabel(portId: string, parameters: Record<string, unknown>): string {
  if (portId === 'switch_default') return 'default'
  const idx = parseInt(portId.split('_')[1], 10)
  const cases = (parameters['cases'] as string[]) ?? []
  return cases[idx] ?? `Case ${idx}`
}

/** Switch port colors (cycling). */
export const SWITCH_PORT_COLORS = [
  '#3b82f6', 'hsl(var(--success))', '#f59e0b', '#a855f7',
  '#ef4444', '#06b6d4', '#f97316', '#ec4899',
]
export const SWITCH_DEFAULT_COLOR = '#9ca3af'
